// Iteration 138 -- Match Availability (coach surface) on event detail.
// One widget per (match, team) the caller manages. Each widget fetches its own
// availability; a 403 means the caller doesn't manage that team, so the widget
// self-hides -- the section therefore needs no role gate of its own and works
// for host- and participating-school coaches alike.

import { useEffect, useState } from "react";
import { CalendarCheck, Check, X, HelpCircle, Clock, Bell, ChevronDown, ChevronRight, Send, Trophy } from "lucide-react";
import type {
  MatchPublic,
  MatchTeamPublic,
  MatchAvailabilityResponse,
  MatchAvailabilityStatus,
} from "@dugads/shared";
import * as api from "@/lib/api";
import { tokens } from "@dugads/ui-tokens";

interface Props {
  matches: MatchPublic[];
  matchTeams: Record<string, MatchTeamPublic[]>;
}

export function AvailabilitySection({ matches, matchTeams }: Props) {
  // (match, team) pairs across the event, in schedule order.
  const pairs = matches.flatMap((m) =>
    (matchTeams[m.id] ?? []).map((mt) => ({ matchId: m.id, matchName: m.name, startsAt: m.starts_at, team: mt })),
  );
  if (pairs.length === 0) return null;

  return (
    <div className="mt-4" data-region="availability">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
        <CalendarCheck className="h-4 w-4" aria-hidden /> Availability
      </h3>
      <div className="space-y-2">
        {pairs.map((p) => (
          <AvailabilityWidget
            key={`${p.matchId}:${p.team.team_id}`}
            matchId={p.matchId}
            matchName={p.matchName}
            matchStartsAt={p.startsAt}
            teamId={p.team.team_id}
            teamName={p.team.team_name}
          />
        ))}
      </div>
    </div>
  );
}

const STATUS_META: Record<
  MatchAvailabilityStatus,
  { label: string; badge: string; Icon: typeof Check }
> = {
  available: { label: "In", badge: "bg-[#E1F5EE] text-[#085041]", Icon: Check },
  out: { label: "Out", badge: "bg-[#FCEBEB] text-[#791F1F]", Icon: X },
  maybe: { label: "Maybe", badge: "bg-[#FAEEDA] text-[#633806]", Icon: HelpCircle },
  no_reply: { label: "No reply", badge: "bg-[#F1EFE8] text-[#444441]", Icon: Clock },
};

function AvailabilityWidget({
  matchId,
  matchName,
  matchStartsAt,
  teamId,
  teamName,
}: {
  matchId: string;
  matchName: string;
  matchStartsAt: string;
  teamId: string;
  teamName: string;
}) {
  const [data, setData] = useState<MatchAvailabilityResponse | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  // 141 -- match-day actions
  const [logisticsOpen, setLogisticsOpen] = useState(false);
  const [logistics, setLogistics] = useState("");
  const [matchDayNote, setMatchDayNote] = useState<string | null>(null);

  // Prefill logistics with a meet-time 60min before kickoff.
  function logisticsTemplate(): string {
    const meet = new Date(new Date(matchStartsAt).getTime() - 60 * 60 * 1000);
    const t = meet.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    return `Match day: ${matchName}.\nMeet by ${t}. Kit: home strip. Bring water + boots.\nReply to let us know you're coming.`;
  }

  useEffect(() => {
    let cancelled = false;
    api
      .getMatchAvailability(matchId, teamId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof api.ApiError && e.status === 403) setForbidden(true);
        else setForbidden(true); // any failure: hide rather than show a broken widget
      });
    return () => {
      cancelled = true;
    };
  }, [matchId, teamId]);

  if (forbidden || data === null) return null;

  async function doRequest() {
    setBusy(true);
    try {
      setData(await api.requestMatchAvailability(matchId, teamId));
      setOpen(true);
    } catch {
      /* surfaced via no state change */
    } finally {
      setBusy(false);
    }
  }

  async function doNudge() {
    setBusy(true);
    try {
      await api.nudgeMatchAvailability(matchId, teamId);
    } finally {
      setBusy(false);
    }
  }

  async function sendLogistics() {
    if (!logistics.trim()) return;
    setBusy(true);
    setMatchDayNote(null);
    try {
      await api.sendMessage({
        context_type: "match",
        context_id: matchId,
        groups: ["parents"],
        content: logistics.trim(),
        response_options: [
          { value: "coming", label: "Coming", color: "green" },
          { value: "not_coming", label: "Can't make it", color: "red" },
        ],
      });
      setLogisticsOpen(false);
      setLogistics("");
      setMatchDayNote("Logistics sent to parents.");
    } catch (e) {
      setMatchDayNote(e instanceof api.ApiError ? e.message : "Failed to send logistics");
    } finally {
      setBusy(false);
    }
  }

  async function postResult() {
    setBusy(true);
    setMatchDayNote(null);
    try {
      const r = await api.broadcastResult(matchId, teamId);
      setMatchDayNote(`Result posted: ${r.summary}`);
    } catch (e) {
      setMatchDayNote(
        e instanceof api.ApiError && e.status === 400
          ? "No final result yet - finalise the score first."
          : e instanceof api.ApiError ? e.message : "Failed to post result",
      );
    } finally {
      setBusy(false);
    }
  }

  const t = data.tally;

  return (
    <div className="rounded-lg border border-border bg-background px-4 py-3" data-availability-team={teamId}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{teamName}</p>
          <p className="truncate text-xs text-muted-foreground">{matchName}</p>
        </div>
        {!data.requested ? (
          <button
            type="button"
            data-action="request-availability"
            onClick={doRequest}
            disabled={busy}
            className="shrink-0 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-50"
          >
            Request availability
          </button>
        ) : (
          <div className="flex shrink-0 items-center gap-2">
            {t.no_reply > 0 && (
              <button
                type="button"
                data-action="nudge-availability"
                onClick={doNudge}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-md border border-input px-2.5 py-1.5 text-sm font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
              >
                <Bell className="h-3.5 w-3.5" aria-hidden /> Nudge {t.no_reply}
              </button>
            )}
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? "Collapse player list" : "Expand player list"}
              className="rounded-md p-1 text-muted-foreground hover:text-foreground"
            >
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        )}
      </div>

      {data.requested && (
        <div className="mt-2">
          <TallyBar tally={t} />
          {open && (
            <ul className="mt-2 divide-y divide-border/60" data-availability-players>
              {data.players.map((p) => {
                const meta = STATUS_META[p.status];
                return (
                  <li key={p.player_id} className="flex items-center justify-between gap-2 py-1.5 text-sm">
                    <span className="truncate text-foreground">{p.player_name}</span>
                    <span className="flex items-center gap-2">
                      {p.note && <span className="truncate text-xs text-muted-foreground">{p.note}</span>}
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.badge}`}>
                        <meta.Icon className="h-3 w-3" aria-hidden /> {meta.label}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* 141 -- match-day actions */}
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border/60 pt-2">
        <button
          type="button"
          data-action="send-logistics"
          onClick={() => { setLogisticsOpen((o) => !o); if (!logistics) setLogistics(logisticsTemplate()); }}
          className="inline-flex items-center gap-1 rounded-md border border-input px-2.5 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted"
        >
          <Send className="h-3.5 w-3.5" aria-hidden /> Send logistics
        </button>
        <button
          type="button"
          data-action="post-result"
          onClick={postResult}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md border border-input px-2.5 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
        >
          <Trophy className="h-3.5 w-3.5" aria-hidden /> Post result
        </button>
        {matchDayNote && <span className="text-xs text-muted-foreground">{matchDayNote}</span>}
      </div>
      {logisticsOpen && (
        <div className="mt-2 flex flex-col gap-2">
          <textarea
            value={logistics}
            onChange={(e) => setLogistics(e.target.value)}
            rows={4}
            aria-label="Logistics message"
            placeholder="Meet time, kit, travel notes..."
            className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
          />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setLogisticsOpen(false)} className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground">
              Cancel
            </button>
            <button
              type="button"
              data-action="send-logistics-confirm"
              onClick={sendLogistics}
              disabled={busy || !logistics.trim()}
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-50"
            >
              Send to parents
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TallyBar({ tally }: { tally: MatchAvailabilityResponse["tally"] }) {
  const segs: Array<{ key: MatchAvailabilityStatus; n: number; color: string }> = [
    { key: "available", n: tally.available, color: tokens.colors.success[700] },
    { key: "maybe", n: tally.maybe, color: tokens.colors.warning[500] },
    { key: "out", n: tally.out, color: tokens.colors.error[700] },
    { key: "no_reply", n: tally.no_reply, color: tokens.colors.neutral[300] },
  ];
  const total = Math.max(1, tally.total);
  return (
    <div>
      <div className="flex h-2 overflow-hidden rounded-full bg-muted">
        {segs.map((s) =>
          s.n > 0 ? (
            <div key={s.key} style={{ width: `${(s.n / total) * 100}%`, backgroundColor: s.color }} />
          ) : null,
        )}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {tally.available} in · {tally.maybe} maybe · {tally.out} out · {tally.no_reply} no reply
      </p>
    </div>
  );
}
